// @ts-check

import { get_list_users } from '~/api/zero-api';
import { MemberActionCell, MemberRoleCell, MemberStatusCell, MemberTeamsCell } from '~/components/AdminPages/MembersDataGrid/MembersDataGrid.cells';
import { AgGridFilter } from '~/other/agGridHelper';
import { memoize } from "lodash-es";
import { generateDataGridEventNames } from '~/components/Shared/ZeroDataGrid/ZeroDataGrid.hooks';
import { UserRole } from '~/other/Constants';

export const MAX_USER_ROWS = 5000;

export const EventNames = generateDataGridEventNames('MembersDataGrid');

export const DEFAULT_SORT_MODEL = [
    {
        colId: "first_name",
        sort: "asc",
    }
];

const statusFormatter = ({value}) => {
    if (typeof value !== "string") return "";
    switch (value) {
        case "pending": return "Invited";
        case "active": return "Registered";
        case "deactivated": return "Deactivated";
        default: return value;
    }
}

/**
 * @returns {AgGrid.GridOptions['columnDefs']}
 */
export function getColumnDefs() {
    /** @type {AgGrid.GridOptions['columnDefs']} */
    let defs = [
        {
            headerName: "",
            field: "_actions_",
            width: 50,
            cellRenderer: MemberActionCell,
            resizable: false,
            sortable: false,
            suppressMovable: true,
            lockPosition: "left",
            cellStyle: {
                padding: 0,
            }
        },
        {
            headerName: "First Name",
            field: "first_name",
            initialSort: "asc",
            width: 200,
            ...AgGridFilter.text,
        },
        {
            headerName: "Last Name",
            field: "last_name",
            width: 200,
            ...AgGridFilter.text,
        },
        {
            headerName: "Email",
            field: "email",
            width: 280,
            ...AgGridFilter.text,
        },
        {
            headerName: "Role",
            field: "role",
            width: 120,
            cellRenderer: MemberRoleCell,
            sortable: false,
            valueFormatter: ({data: user}) => {
                if (!user) return "";
                return UserRole.mapToName(user.role);
            },
            filter: "agSetColumnFilter",
            filterParams: {
                values: [
                    null,
                    "admin",
                    "team_lead",
                    "user",
                    "user_restricted",
                    "viewer"
                ],
                suppressSorting: true,
                valueFormatter: ({value}) => value === null ? "(Blank)" : UserRole.mapToName(value),
            },
        },
        {
            headerName: "Status",
            field: "status",
            width: 120,
            cellRenderer: MemberStatusCell,
            sortable: false,
            valueFormatter: statusFormatter,
            filter: "agSetColumnFilter",
            filterParams: {
                values: [
                    "active",
                    "pending",
                    "deactivated",
                ],
                suppressSorting: true,
                valueFormatter: statusFormatter,
            },
        },
        {
            headerName: "Teams",
            field: "teams",
            width: 260,
            cellRenderer: MemberTeamsCell,
            sortable: false,
            valueFormatter: ({data: user}) => {
                if (!user) return "";
                const teams = user.teams || [];
                return teams.map(team => team.name).join(", ");
            },
        },
        {
            headerName: "Title",
            field: "title",
            width: 280,
            ...AgGridFilter.text,
        }
    ];

    return defs;
}

/**
 * @param {URLSearchParams} queryParams 
 * @returns {Promise<{users: any[], totalUsers: number}>}
 */
async function _loadUsers(queryParams) {
    const response = await get_list_users(`?${queryParams}`);
    const content = await response.json();
    const users = content.users ?? [];
    const totalUsers = content.total_users ?? 0;

    return { users, totalUsers };
}

export const loadUsers = memoize(_loadUsers, (queryParams) => queryParams.toString());
